/* eslint-disable @typescript-eslint/no-explicit-any */


import * as L from 'leaflet';
import * as G from 'geojson';
import Typography from '@mui/material/Typography';
import { PaintLineString, PaintPolygon } from '../mapa';
import { Mancha } from '../../context';
import { Box, Divider } from '@mui/material';
import { HidroDXEditor } from '../edit';

export type PolyManchaData = Omit<Mancha, 'polygon'>;

type LatLngExpression = L.LatLngExpression;

export class PolyMancha extends L.Layer {
  // Propriedades de dados/estilo
  public properties?: PolyManchaData;
  public paintOptions: PaintLineString;

  // interno: Polygon OU LayerGroup<Polygon>
  private inner: | L.Polygon<PolyManchaData> | L.LayerGroup<L.Polygon<PolyManchaData>>;
  private map?: L.Map;

  constructor(
    geometries:
      | LatLngExpression[]              // 1 ring
      | LatLngExpression[][]            // polygon com holes
      | LatLngExpression[][][],         // vários polígonos -> LayerGroup
    properties?: PolyManchaData,
    paintOptions?: PaintPolygon
  ) {
    super();

    this.properties = properties;
    this.paintOptions = paintOptions ?? {
      color: 'rgba(255, 255, 0, 1)',
      weight: 2,
    };

    const isGroup =
      Array.isArray(geometries) &&
      Array.isArray((geometries as any)[0]) &&
      Array.isArray((geometries as any)[0][0]);

    if (isGroup) {
      const polys = (geometries as LatLngExpression[][][]).map((coords) =>
        L.polygon<PolyManchaData>(coords, this.paintOptions)
      );
      this.inner = L.layerGroup<L.Polygon<PolyManchaData>>(polys);

      this.forEachPolygon((p) => {
        p.on({
          mouseover: this.highlightFeature,
          mouseout: this.resetStyle,
          click: (e) => {
            if (!this.map) return;

            if (this.paintOptions.bindPopUp) {
              const popup = L.popup({ maxWidth: 390 })
                .setLatLng(e.latlng)
                .setContent(this.popUpComponent());
              popup.openOn(this.map);
            }
          },
        });
      });
    } else {
      this.inner = L.polygon<PolyManchaData>(geometries as any, this.paintOptions);

      this.inner.on({
        mouseover: this.highlightFeature,
        mouseout: this.resetStyle,
        click: (e) => {
          if (!this.map) return;

          if (this.paintOptions.bindPopUp) {
            const popup = L.popup({ maxWidth: 390 })
              .setLatLng(e.latlng)
              .setContent(this.popUpComponent());
            popup.openOn(this.map);
          }
        },
      });
    }
  }

  private isPolygon(x?: L.Polygon<PolyManchaData> | L.LayerGroup<L.Polygon<PolyManchaData>>): x is L.Polygon<PolyManchaData> {
    return (x ?? this.inner) instanceof L.Polygon;
  }

  private forEachPolygon(cb: (p: L.Polygon<PolyManchaData>) => void) {
    if (this.isPolygon(this.inner)) {
      cb(this.inner);
    } else {
      this.inner.eachLayer((layer) => {
        if (layer instanceof L.Polygon) cb(layer);
      });
    }
  }

  // obrigatório
  onAdd(map: L.Map): this {
    this.map = map;
    this.inner.addTo(map);
    return this;
  }

  // obrigatório
  onRemove(map: L.Map): this {
    this.inner.removeFrom(map);
    return this;
  }

  /** Adiciona ao mapa */
  addTo(map: L.Map): this {
    this.inner.addTo(map);
    return this;
  }

  /** Remove do mapa */
  remove(): this {
    this.inner.remove();
    return this;
  }

  /** Zas para Geojson */
  toGeoJSON(precision?: number | false) {
    if (this.isPolygon()) {
      return {
        type: "Feature",
        geometry: (this.inner.toGeoJSON(precision) as G.Feature<G.Polygon | G.MultiPolygon, PolyManchaData>).geometry,
        properties: this.properties,
      } as G.Feature<G.Polygon, PolyManchaData | undefined>;
    }

    const inData = this.inner as L.LayerGroup<L.Polygon<PolyManchaData>>;

    const coordinates = inData.getLayers()
      .filter((l): l is L.Polygon<PolyManchaData> => l instanceof L.Polygon)
      .map((layer) => {
        const geo = layer.toGeoJSON(precision);
        if (geo.geometry.type !== "Polygon") {
          throw new Error("Esperado um Polygon no LayerGroup");
        }
        return geo.geometry.coordinates;
      });

    return {
      type: "Feature",
      geometry: {
        type: "MultiPolygon",
        coordinates: coordinates,
      } as G.MultiPolygon,
      properties: this.properties,
    } as G.Feature<G.MultiPolygon, PolyManchaData | undefined>;
  }

  /** Bounds do(s) polígono(s) */
  getBounds(): L.LatLngBounds {
    if (this.isPolygon(this.inner)) {
      return this.inner.getBounds();
    }
    let bounds: L.LatLngBounds | null = null;
    this.forEachPolygon((p) => {
      const b = p.getBounds();
      bounds = bounds ? bounds.extend(b) : L.latLngBounds(b.getSouthWest(), b.getNorthEast());
    });
    return bounds ?? L.latLngBounds([]);
  }

  /** Estilo (aplica a todos os polígonos no grupo) */
  setStyle(style?: L.PathOptions): this {
    if (!style) return this;
    this.forEachPolygon((p) => p.setStyle(style));
    return this;
  }

  /** Bind de popup em todos os polígonos */
  bindPopup(
    content: L.Popup | string | HTMLElement,
    opts?: L.PopupOptions
  ): this {
    this.forEachPolygon((p) => p.bindPopup(content as any, opts));
    return this;
  }

  /** Bind de tooltip em todos os polígonos */
  bindTooltip(
    content: L.Tooltip | string | HTMLElement,
    opts?: L.TooltipOptions
  ): this {
    this.forEachPolygon((p) => p.bindTooltip(content as any, opts));
    return this;
  }

  /** Clone Layer */
  clone() {
    const geometries: L.LatLngExpression[][][] = [];

    if (this.inner instanceof L.Polygon) {
      const coords = this.inner.getLatLngs();
      geometries.push(coords as L.LatLngExpression[][]);
    } else {
      this.inner.eachLayer((layer) => {
        if (layer instanceof L.Polygon) {
          geometries.push(layer.getLatLngs() as L.LatLngExpression[][]);
        }
      });
    }

    return new PolyMancha(
      geometries.length === 1 ? geometries[0] : geometries,
      this.properties ? { ...this.properties } : undefined,
      this.paintOptions ? { ...this.paintOptions } : undefined
    );
  }

  /** Obtendo as LatLons */
  getLatLngs(): L.LatLngExpression[][] | L.LatLngExpression[][][] {
    if (this.inner instanceof L.Polygon) {
      return this.inner.getLatLngs() as L.LatLngExpression[][];
    }

    const geometries: L.LatLngExpression[][][] = [];

    this.inner.eachLayer((layer) => {
      if (layer instanceof L.Polygon) {
        geometries.push(layer.getLatLngs() as L.LatLngExpression[][]);
      }
    });

    return geometries;
  }

  /** Getter de bbox como propriedade calculada */
  get bbox(): L.LatLngBounds {
    return this.getBounds();
  }

  /** Acesso ao objeto interno, se necessário */
  get raw() {
    return this.inner;
  }

  private resetStyle = () => this.setStyle(this.paintOptions);

  private highlightFeature = () => {
    if (this.paintOptions.paintHovered) {
      this.setStyle(this.paintOptions.paintHovered);
    }
  };

  private clickFeature = () => {
    if (this.paintOptions.paintClick) {
      this.setStyle(this.paintOptions.paintClick);
    }
  };

  public popUpComponent() {
    return window.JSXToHTML(
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyItems: 'center',
          overflowY: 'auto',
          height: '10em',
          width: '19em',
          gap: '0.5em',
        }}
      >
        <Typography fontWeight="bold" fontSize={16} textAlign="center">
          {this.properties?.nome ?? 'Mancha'}
        </Typography>
        <Divider />

        <Box sx={{ textAlign: "justify", flex: 1 }}>
          <HidroDXEditor
            readOnly
            image={false}
            chartMap={false}
            startText={this.properties?.descricao ?? ""}
          />
        </Box>
      </div>
    );
  }

  public tooltipComponent() {
    return window.JSXToHTML(
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          justifyItems: 'center',
          overflowY: 'auto',
          height: '10em',
          width: '19em',
          gap: '0.5em',
        }}
      >
        <Typography fontWeight="bold" fontSize={16} textAlign="center">
          {this.properties?.nome ?? 'Mancha'}
        </Typography>
        <Divider />

        <Box sx={{ textAlign: "justify", flex: 1 }}>
          <HidroDXEditor
            readOnly
            image={false}
            chartMap={false}
            startText={this.properties?.descricao ?? ""}
          />
        </Box>
      </div>
    );
  }
}
